import { useCallback, useEffect, useState } from 'react'
import { cachedLayoutCatalog, loadLayoutCatalog } from './layoutCatalogCache'

const EMPTY_CATALOG = { integrations: [], applications: [] }

// Kept independent from portal config so the signed-out build can load the
// shared catalog adapter without importing any authenticated app bootstrap.
export default function useLayoutCatalogFromUrl (catalogApiUrl) {
  // A catalog already loaded (or prefetched) this session renders at once, without a spinner.
  const [catalog, setCatalog] = useState(() => cachedLayoutCatalog(catalogApiUrl) || EMPTY_CATALOG)
  const [loading, setLoading] = useState(() => !cachedLayoutCatalog(catalogApiUrl))
  const [error, setError] = useState(null)
  // Bumped by refetch() to re-run the fetch effect (e.g. the dialog's Retry after a load failure).
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    let cancelled = false
    const cached = reloadToken === 0 ? cachedLayoutCatalog(catalogApiUrl) : null
    if (cached) {
      setCatalog(cached)
      setLoading(false)
      setError(null)
      return
    }
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const loaded = await loadLayoutCatalog(catalogApiUrl, { force: reloadToken > 0 })
        if (!cancelled) setCatalog(loaded)
      } catch (err) {
        if (!cancelled) setError(err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [catalogApiUrl, reloadToken])

  const refetch = useCallback(() => setReloadToken(token => token + 1), [])

  return { catalog, loading, error, refetch }
}
